import { Box } from '@mui/material';
import {
  add,
  differenceInDays,
  getDay,
  getMonth,
  getYear,
  set,
} from 'date-fns';
import React, { useEffect, useState } from 'react';
import { useResizeDetector } from 'react-resize-detector';
import {
  Area,
  Bar,
  CartesianGrid,
  ComposedChart,
  Label,
  Legend,
  Line,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from 'recharts';
import {
  commaFormat,
  getDayOfWeek,
  getMonthListByMax,
} from '../../common/helper';
import NoDataPage from '../commonComponent/NoDataPage';
import { SwMedalLegend } from '../commonComponent/SwMedalLegend';

interface Props {
  stepsGoalRateAverage?: boolean[];
  data?: any[];
  startDate: number;
  totalAverage: number;
  from?: string;
  isPdf?: boolean;
}

export const RwwGraphByMedal: React.FC<Props> = ({
  stepsGoalRateAverage = [false, false],
  data,
  startDate,
  totalAverage,
  from = 'dashboard',
  isPdf = false,
}) => {
  const [rowCount, setRowCount] = useState<number>(1);
  const [rateData, setRateData] = useState<any>([]);
  const { width, ref } = useResizeDetector();
  const showLegend = false;
  let maxValue = 0;

  useEffect(() => {
    if (data) {
      setRowCount(Math.ceil(data.length / 11));
      const newMonths = getMonthListByMax(getMonth(startDate * 1000) + 1);
      const newData = newMonths.map((month: string, idx: number) => {
        const monthData = data.filter((item: any) => item.month === month);
        const newGoldCount =
          monthData !== null && monthData.length > 0
            ? monthData[0].femaleGoldCount + monthData[0].maleGoldCount
            : 0;
        const newSilverCount =
          monthData !== null && monthData.length > 0
            ? monthData[0].femaleSilverCount + monthData[0].maleSilverCount
            : 0;
        const newBronzeCount =
          monthData !== null && monthData.length > 0
            ? monthData[0].femaleBronzeCount + monthData[0].maleBronzeCount
            : 0;
        const newTotal = newGoldCount + newSilverCount + newBronzeCount;
        maxValue = newTotal > maxValue ? newTotal : maxValue;
        return {
          month: month,
          전체: newTotal,
          금: newGoldCount,
          은: newSilverCount,
          동: newBronzeCount,
        };
      });
      setRateData(newData);
    }
  }, [data]);

  return (
    <>
      <div className='basic_tab_content tab_content' ref={ref}>
        {data && data.length ? (
          <>
            <div
              className='bg_lightgray'
              style={{ width: width, height: '500px', backgroundColor: '#fff' }}
            >
              {!showLegend && <SwMedalLegend />}

              <ResponsiveContainer width='100%' height='100%'>
                <ComposedChart
                  width={500}
                  height={400}
                  data={rateData}
                  margin={{
                    top: 20,
                    right: 20,
                    bottom: 20,
                    left: 20,
                  }}
                >
                  <defs>
                    <linearGradient id='mcolor' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#f7db07' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#ebf08e' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='mcolor2' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#dadbdd' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#92BDFF' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='mcolor3' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#62f667' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#9df3aa' stopOpacity={0} />
                    </linearGradient>
                    <linearGradient id='mcolor4' x1='0' y1='0' x2='1' y2='1'>
                      <stop offset='5%' stopColor='#FF4975' stopOpacity={0.8} />
                      <stop offset='95%' stopColor='#FFA88C' stopOpacity={0} />
                    </linearGradient>
                  </defs>
                  <CartesianGrid stroke='#f5f5f5' />
                  <XAxis
                    dataKey='month'
                    unit='월'
                    tickLine={false}
                    padding={{ left: 30, right: 30 }}
                  />
                  <YAxis
                    yAxisId='left'
                    unit=''
                    tickLine={false}
                    domain={[0, Math.round(maxValue)]}
                  />
                  <Tooltip
                    // eslint-disable-next-line react/no-unstable-nested-components
                    content={({ active, payload, label }: any) => {
                      if (active && payload && payload.length) {
                        return (
                          <Box className='graph_tooltip_box'>
                            <p className='graph_tooltip_title'>
                              {payload[0].payload.month}월
                            </p>
                            <div className='graph_tooltip_content_box'>
                              <p>금</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['금']}
                              </p>
                            </div>
                            <div className='graph_tooltip_content_box'>
                              <p>은</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['은']}
                              </p>
                            </div>
                            <div className='graph_tooltip_content_box'>
                              <p>동</p>
                              <p className='graph_tooltip_content_box_desc'>
                                {payload[0].payload['동']}
                              </p>
                            </div>
                          </Box>
                        );
                      }
                      return null;
                    }}
                    cursor={false}
                  />
                  {showLegend && (
                    <Legend verticalAlign='top' height={36} iconType='circle' />
                  )}
                  <Bar
                    yAxisId='left'
                    dataKey='금'
                    barSize={10}
                    fill='url(#mcolor)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='은'
                    barSize={10}
                    fill='url(#mcolor2)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='동'
                    barSize={10}
                    fill='url(#mcolor3)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                  <Bar
                    yAxisId='left'
                    dataKey='전체'
                    barSize={10}
                    fill='url(#mcolor4)'
                    radius={[12, 12, 0, 0]}
                    legendType='circle'
                  />
                </ComposedChart>
              </ResponsiveContainer>
            </div>
            <div style={{ paddingTop: '2rem' }}>
              <div className='table_style01'>
                {!isPdf && (
                  <div>
                    <table style={{ whiteSpace: 'nowrap' }}>
                      <thead>
                        <tr>
                          <th>월</th>
                          {rateData.map((d: any, idx: number) => (
                            <th key={idx.toString()}>{d.month}월</th>
                          ))}
                        </tr>
                      </thead>
                      <tbody>
                        <tr>
                          <td>전체</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>
                              {commaFormat(d['전체'])}
                            </td>
                          ))}
                        </tr>
                        <tr>
                          <td>금</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{commaFormat(d['금'])}</td>
                          ))}
                        </tr>
                        <tr>
                          <td>은</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{commaFormat(d['은'])}</td>
                          ))}
                        </tr>
                        <tr>
                          <td>동</td>
                          {rateData.map((d: any, idx: number) => (
                            <td key={idx.toString()}>{commaFormat(d['동'])}</td>
                          ))}
                        </tr>
                      </tbody>
                    </table>
                  </div>
                )}
                {isPdf && (
                  <div>
                    {Array(rowCount)
                      .fill(0)
                      .map((_d, idx) => (
                        <table
                          key={idx.toString()}
                          style={{ whiteSpace: 'nowrap' }}
                        >
                          <colgroup>
                            {Array(12)
                              .fill(0)
                              .map((d, subidx) => (
                                <col
                                  key={subidx.toString()}
                                  width={`${100 / 12}%`}
                                />
                              ))}
                          </colgroup>
                          <thead>
                            <tr>
                              <th>월</th>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <th key={subidx.toString()}>{d.month}월</th>
                                ))}
                            </tr>
                          </thead>
                          <tbody>
                            <tr>
                              <td>금</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['금'])}
                                  </td>
                                ))}
                            </tr>
                            <tr>
                              <td>은</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['은'])}
                                  </td>
                                ))}
                            </tr>
                            <tr>
                              <td>동</td>
                              {rateData
                                ?.slice(11 * idx, 11 * (idx + 1))
                                .map((d: any, subidx: number) => (
                                  <td key={subidx.toString()}>
                                    {commaFormat(d['동'])}
                                  </td>
                                ))}
                            </tr>
                          </tbody>
                        </table>
                      ))}
                  </div>
                )}
              </div>
            </div>
          </>
        ) : (
          <Box sx={{ height: '500px' }}>
            <NoDataPage />
          </Box>
        )}
      </div>
    </>
  );
};
